import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { fmtPrice } from "../components/Reveal";
import { useStore } from "../context/StoreContext";
import { useLang } from "../context/LanguageContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const { clearCart } = useStore();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 8) { if (active) setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  return (
    <div className="pt-40 min-h-screen flex items-start justify-center px-6" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="max-w-lg w-full text-center py-16">
        {status === "checking" && (
          <><Loader2 className="w-12 h-12 mx-auto text-gold animate-spin mb-6" />
            <h1 className="font-display font-extrabold text-3xl">{t("success.confirming")}</h1>
            <p className="text-ink/50 mt-3 font-mono text-sm">{t("success.dontClose")}</p></>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 bg-gold text-inverse flex items-center justify-center mx-auto mb-8"><Check className="w-8 h-8" /></div>
            <h1 className="font-display font-extrabold tracking-tighter text-[clamp(2rem,5vw,3.5rem)] leading-none" data-testid="payment-paid-title">{t("success.paidTitle")}</h1>
            <p className="text-ink/55 mt-4">{t("success.thanks")}</p>
            {data?.amount != null && <p className="font-display font-extrabold text-3xl mt-6">{fmtPrice(data.amount)}</p>}
            <Link to="/catalogo" className="inline-flex items-center gap-2 mt-10 bg-blue text-[color:var(--bg)] px-8 py-4 hover:bg-gold hover:text-inverse transition-colors font-medium">{t("success.keep")} <ArrowRight className="w-4 h-4" /></Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="w-16 h-16 bg-ink/10 flex items-center justify-center mx-auto mb-8"><AlertCircle className="w-8 h-8 text-ink/50" /></div>
            <h1 className="font-display font-extrabold text-3xl">{status === "timeout" ? t("success.processing") : t("success.error")}</h1>
            <p className="text-ink/55 mt-4">{status === "timeout" ? t("success.processingDesc") : t("success.errorDesc")}</p>
            <Link to="/carrito" className="inline-flex items-center gap-2 mt-10 border border-ink/15 px-8 py-4 hover:border-gold hover:text-gold transition-colors font-medium">{t("success.back")}</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
